import { type QueryKey, skipToken, useInfiniteQuery } from '@tanstack/react-query';
import { isObject } from 'lodash';

import { Agents } from '@air/api';
import { callAirApi } from '@air/utils-api';

export const PRIVATE_WORKSPACE_AGENT_SESSIONS_LIST_INFINITE_KEY = 'PRIVATE_WORKSPACE_AGENT_SESSIONS_LIST_INFINITE';

export type PrivateWorkspaceAgentSessionsListInfiniteParams = Omit<
  NonNullable<Parameters<typeof Agents.Sessions.list>[0]>,
  'agentType' | 'workspaceId' | 'cursor'
> & {
  workspaceId: string | undefined;
};

export const buildPrivateWorkspaceAgentSessionsListInfiniteParams = (
  workspaceId: string,
  extra?: Omit<PrivateWorkspaceAgentSessionsListInfiniteParams, 'workspaceId'>,
): PrivateWorkspaceAgentSessionsListInfiniteParams => ({
  workspaceId,
  ...extra,
});

/** Backend default list excludes archived sessions — fetch them with an explicit status filter. */
export const buildPrivateWorkspaceAgentSessionsListArchivedParams = (
  workspaceId: string,
): PrivateWorkspaceAgentSessionsListInfiniteParams =>
  buildPrivateWorkspaceAgentSessionsListInfiniteParams(workspaceId, { status: 'archived' });

export type PrivateWorkspaceAgentSessionsListInfiniteKey = [
  typeof PRIVATE_WORKSPACE_AGENT_SESSIONS_LIST_INFINITE_KEY,
  PrivateWorkspaceAgentSessionsListInfiniteParams,
];

export const getPrivateWorkspaceAgentSessionsListInfiniteKey = (
  params: PrivateWorkspaceAgentSessionsListInfiniteParams,
): PrivateWorkspaceAgentSessionsListInfiniteKey => [PRIVATE_WORKSPACE_AGENT_SESSIONS_LIST_INFINITE_KEY, params];

export const isPrivateWorkspaceAgentSessionsListInfiniteKey = (
  key: QueryKey,
): key is PrivateWorkspaceAgentSessionsListInfiniteKey =>
  Array.isArray(key) && key[0] === PRIVATE_WORKSPACE_AGENT_SESSIONS_LIST_INFINITE_KEY && isObject(key[1]);

export type UsePrivateWorkspaceAgentSessionsListInfiniteOptions = {
  enabled?: boolean;
};

export const usePrivateWorkspaceAgentSessionsListInfinite = (
  params: PrivateWorkspaceAgentSessionsListInfiniteParams,
  options?: UsePrivateWorkspaceAgentSessionsListInfiniteOptions,
) => {
  const { workspaceId } = params;

  const queryFn = !workspaceId
    ? skipToken
    : ({ pageParam }: { pageParam: string }) =>
        callAirApi(() =>
          Agents.Sessions.list({
            agentType: 'workspace',
            ...params,
            workspaceId,
            cursor: pageParam || undefined,
          }),
        );

  return useInfiniteQuery({
    queryKey: getPrivateWorkspaceAgentSessionsListInfiniteKey(params),
    queryFn,
    initialPageParam: '',
    getNextPageParam: (lastPage) => (lastPage.pagination?.hasMore ? (lastPage.pagination.cursor ?? null) : null),
    enabled: !!workspaceId && (options?.enabled ?? true),
  });
};
